import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, Mail, X } from 'lucide-react';
import { fetchApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { clearLinkToken, readLinkToken, takeNotice } from '../utils/authLinks';
import '../styles/account.css';

/**
 * Persistent reminder that the address behind this account has never been
 * confirmed, plus the resend button that makes the reminder actionable.
 *
 * It also consumes a `?verify=<token>` link for a signed-in user: the letter goes
 * to somebody who is usually already logged in, so the confirmation has to work
 * from inside the application, not only from the sign-in screen.
 *
 * Mounted once in the application shell, above the routed view.
 */
export default function EmailVerificationBanner() {
  const { user, refreshUser } = useAuth();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [handoff, setHandoff] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);

  // One-shot message left behind by the sign-in screen (invitation outcome).
  useEffect(() => { setHandoff(takeNotice()); }, []);

  useEffect(() => {
    const token = readLinkToken('verify');
    if (!token) return;
    clearLinkToken('verify');
    fetchApi('/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) })
      .then(async () => {
        setConfirmed(true);
        setResult('Email подтверждён. Подключение каналов и оплата тарифа доступны.');
        await refreshUser().catch(() => undefined);
      })
      .catch((error: any) => {
        setFailure(error.message || 'Не удалось подтвердить email. Запросите новое письмо.');
      });
    // Mount-only on purpose: the token is single-use, so re-running this effect
    // would replay a link that has already been consumed.
  }, []);

  const resend = async () => {
    setSending(true);
    setFailure(null);
    setResult(null);
    try {
      const data = await fetchApi('/auth/resend-verification', { method: 'POST' });
      setResult(data?.message || 'Письмо отправлено. Проверьте входящие и папку «Спам».');
      if (data?.alreadyVerified) await refreshUser().catch(() => undefined);
    } catch (error: any) {
      setFailure(error.message || 'Не удалось отправить письмо. Повторите позже.');
    } finally {
      setSending(false);
    }
  };

  if (!user) return null;

  const verified = Boolean(user.emailVerified) || confirmed;

  return (
    <>
      {handoff && (
        <div className={`verify-banner${handoff.tone === 'info' ? ' done' : ''}`} role="status">
          <Info size={18} aria-hidden="true" />
          <div className="verify-banner-text"><strong>{handoff.text}</strong></div>
          <div className="verify-banner-actions">
            <button type="button" className="ghost" onClick={() => setHandoff(null)} aria-label="Закрыть сообщение">
              <X size={15} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {verified && result && (
        <div className="verify-banner done" role="status">
          <CheckCircle2 size={18} aria-hidden="true" />
          <div className="verify-banner-text"><strong>{result}</strong></div>
          <div className="verify-banner-actions">
            <button type="button" className="ghost" onClick={() => setResult(null)} aria-label="Закрыть сообщение">
              <X size={15} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {!verified && (
        <div className="verify-banner" role="status">
          <AlertTriangle size={18} aria-hidden="true" />
          <div className="verify-banner-text">
            <strong>Подтвердите email {user.email}</strong>
            <span>
              {failure
                || result
                || 'Пока адрес не подтверждён, нельзя подключать каналы связи и оплачивать тариф. Диалоги, сделки и каталог работают как обычно.'}
            </span>
          </div>
          <div className="verify-banner-actions">
            <button type="button" onClick={resend} disabled={sending}>
              <Mail size={14} aria-hidden="true" /> {sending ? 'Отправляем…' : 'Отправить письмо'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
